import CodeMirror from '@github-ui/code-mirror'
import {noop} from '@github-ui/noop'
import {keepPreviousData, useQuery} from '@github-ui/react-query'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {StopIcon} from '@primer/octicons-react'
import {Spinner} from '@primer/react'
import {SkeletonText, type SkeletonTextProps} from '@primer/react/experimental'
import {clsx} from 'clsx'
import throttle from 'lodash-es/throttle'
import {useEffect, useId, useMemo, useState} from 'react'

import {useGuessCodeMirrorSettings} from '../../hooks/use-code-mirror-config'
import styles from './MarkdownPreview.module.css'

interface MarkdownResponse {
  html: SafeHTMLString
}

export interface MarkdownContent {
  value: string
  name: string
}

interface LoadingSkeletonOptions {
  size: SkeletonTextProps['size']
  lines: number
}

export interface MarkdownPreviewProps {
  content: MarkdownContent
  className?: string
  /** When true, displays raw markdown content using CodeMirror if rendering as HTML fails. Defaults to false. */
  fallbackToRaw?: boolean
  /** When true, hides the anchor links that appear next to headings. Defaults to false. */
  hideHeadingAnchors?: boolean
  /** When defined, use a loading skeleton instead of a spinner with the provided options */
  loadingSkeletonOptions?: LoadingSkeletonOptions[]
}

export function MarkdownPreview({
  content,
  className,
  fallbackToRaw = false,
  hideHeadingAnchors = false,
  loadingSkeletonOptions,
}: MarkdownPreviewProps) {
  const throttledValue = useThrottledValue(content.value, content.name, 2000)
  const {status, data} = useQuery({
    queryKey: ['copilot-immersive', 'MarkdownPreview', throttledValue, content.name],
    queryFn: async () => {
      const res = await verifiedFetchJSON('/copilot/immersive/markdown', {
        method: 'POST',
        body: {content: throttledValue, filename: content.name},
      })
      if (!res.ok) {
        throw new Error(`Failed to load rendered markdown (${res.status} on ${res.url})`)
      }
      const value = await res.json()
      return value as MarkdownResponse
    },
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  })

  return (
    <div
      className={clsx(
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        'readme html-blob blob',
        styles.markdownPreview,
        hideHeadingAnchors && styles.hideHeadingAnchors,
        className,
      )}
    >
      {status === 'pending' ? (
        loadingSkeletonOptions ? (
          <div aria-label="Loading Markdown preview…" role="status" aria-live="polite">
            {loadingSkeletonOptions.map((options, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <div key={index} className="tmp-mb-3">
                <SkeletonText size={options.size} lines={options.lines} />
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.spinner}>
            <Spinner srText="Loading Markdown preview…" />
          </div>
        )
      ) : status === 'error' ? (
        fallbackToRaw ? (
          <RawMarkdownPreview content={content} />
        ) : (
          <div className={styles.error}>
            <span className={styles.errorIcon}>
              <StopIcon size={24} />
            </span>
            <h2 className={styles.errorHeader}>Markdown rendering failed</h2>
            <p>Refresh the page or check the markdown code</p>
          </div>
        )
      ) : (
        <VerifiedHTMLDiv className="markdown-body" html={data.html} />
      )}
    </div>
  )
}

/**
 * Raw markdown viewer component that displays markdown as plain text using CodeMirror
 */
function RawMarkdownPreview({content}: {content: MarkdownContent}) {
  const labelId = useId()
  const codeMirrorOptions = useGuessCodeMirrorSettings({
    value: content.value,
    language: 'markdown',
  })

  return (
    <>
      <span id={labelId} className="sr-only">
        {content.name} raw markdown contents
      </span>
      <CodeMirror
        ariaLabelledBy={labelId}
        fileName={content.name}
        isReadOnly
        spacing={codeMirrorOptions}
        value={content.value}
        onChange={noop} // Read-only
        hideHelp
      />
    </>
  )
}

/**
 * Returns the value given to it, eventually. The return value changes at most every delay milliseconds.
 * If the resetKey changes, the throttled value is immediately updated and throttling restarts.
 */
function useThrottledValue<T>(value: T, resetKey: string, delay: number) {
  const [throttledValue, setThrottledValue] = useState(value)
  const [lastResetKey, setLastResetKey] = useState(resetKey)
  const throttledSetter = useMemo(() => throttle(setThrottledValue, delay), [delay])

  useEffect(() => {
    // If the reset key changed, immediately update the value and reset throttling
    if (resetKey !== lastResetKey) {
      throttledSetter.cancel() // Cancel any pending throttled calls
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setThrottledValue(value)
      setLastResetKey(resetKey)
    } else {
      throttledSetter(value)
    }
  }, [throttledSetter, value, resetKey, lastResetKey])

  return throttledValue
}
